import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
} from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { fetchQuizResultsByUserId } from '../services/firestore/quizResultService';
import { QuizResult } from '@e-learning/shared/src/store/types';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { courses } = useAppSelector((state) => state.courses);
  const [results, setResults] = React.useState<QuizResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchQuizResultsByUserId(user.uid)
        .then(setResults)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="info">Please log in to view your dashboard.</Alert>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const totalQuizzes = results.length;
  const averageScore =
    totalQuizzes > 0
      ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / totalQuizzes)
      : 0;
  const coursesAttempted = new Set(results.map((r) => r.courseId)).size;
  const recentResults = results.slice(0, 10);

  const getCourseName = (courseId: string) => {
    const course = courses.find((c) => c.id === courseId);
    return course?.title || courseId;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <AccountCircleIcon sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" component="h1">
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <QuizIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Total Quizzes</Typography>
                </Box>
                <Typography variant="h3">{totalQuizzes}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Average Score</Typography>
                </Box>
                <Typography variant="h3">{averageScore}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Courses Attempted
                </Typography>
                <Typography variant="h3">{coursesAttempted}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h5" gutterBottom>
          Recent Quiz Activity
        </Typography>
        {recentResults.length === 0 ? (
          <Alert severity="info">No quiz history yet. Take a quiz to see your results here!</Alert>
        ) : (
          <List>
            {recentResults.map((result) => (
              <Card key={result.id} sx={{ mb: 2 }}>
                <CardContent>
                  <ListItem>
                    <ListItemText
                      primary={getCourseName(result.courseId)}
                      secondary={`${new Date(result.timestamp).toLocaleString()}`}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={`${result.score}%`}
                        color={result.score >= 70 ? 'success' : result.score >= 50 ? 'warning' : 'error'}
                      />
                      <Button
                        size="small"
                        onClick={() => navigate(`/courses/${result.courseId}`)}
                      >
                        View Course
                      </Button>
                    </Box>
                  </ListItem>
                </CardContent>
              </Card>
            ))}
          </List>
        )}

        {results.length > 10 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/quiz-history')}>
              View All Quiz History
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DashboardPage;

